// Conception d'un dossier : trois espaces distincts — le schéma fonctionnel (relations du système),
// les cheminements (passages physiques des câbles sur un plan) et le dimensionnement CVC (bilans froid et chaud).
import { useParams, useSearchParams } from 'react-router';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import { Onglets } from '../../components/ui.tsx';
import { OngletSchema } from './EditeurSchema.tsx';
import { OngletCheminements } from './OngletCheminements.tsx';
import { OngletDimensionnement } from '../cvc/OngletDimensionnement.tsx';
import { OngletComptage } from './OngletComptage.tsx';
import { OngletProposition } from './proposition/OngletProposition.tsx';

type OngletConception = 'schema' | 'cheminements' | 'comptage' | 'cvc' | 'proposition';

const INTRO: Record<OngletConception, string> = {
  comptage: 'Comptez les ouvrages sur chaque plan et rattachez-les aux étages / zones. Seul un comptage validé alimente le chiffrage proposé.',
  schema: 'Relations du système : équipements, alimentations, bus, passerelles, réseaux IP et leurs dépendances. Une compatibilité n’est « étayée » qu’avec une source documentaire ; sinon elle reste « à vérifier ».',
  cheminements: 'Passages des câbles et liaisons frigorifiques sur plan, montées entre étages et charge complémentaire sourcée. Les métrés doivent être validés par le référent ; réserve et chutes restent séparées.',
  proposition: 'TRAME propose par ses calculs l’implantation des unités et le réseau frigorifique sur le plan, la met en visuel et la compare à la proposition du technicien. Calcul déterministe d’avant-projet, à valider.',
  cvc: 'Pré-dimensionnement climatisation, chauffage et ventilation : bilans froid et chaud par zone et par groupe extérieur, température extérieure de base de la table officielle, hypothèses visibles et modifiables, note de calcul.',
};
const LIBELLES: Record<OngletConception, string> = { schema: 'Schéma fonctionnel', cheminements: 'Cheminements', comptage: 'Comptage', cvc: 'Dimensionnement CVC', proposition: 'Proposition TRAME' };
// Sur téléphone, libellés courts : les trois espaces tiennent sur une ligne (le contenu ne descend pas sous la ligne de flottaison).
const COURTS: Record<OngletConception, string> = { schema: 'Schéma', cheminements: 'Chemins', comptage: 'Compter', cvc: 'CVC', proposition: 'Proposer' };

export function PageConception() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const vue = params.get('vue');
  const actif: OngletConception = vue === 'cheminements' || vue === 'cvc' || vue === 'comptage' || vue === 'proposition' ? vue : 'schema';
  const changer = (o: OngletConception) => {
    const n = new URLSearchParams(params);
    n.delete('etape'); n.delete('variante');
    if (o === 'schema') { n.delete('vue'); n.delete('plan'); } else { n.set('vue', o); if (o === 'cvc') n.delete('plan'); }
    setParams(n, { replace: true });
  };
  if (!id) return null;
  return (
    <DossierCadre dossierId={id} actif="conception" large>
      <div className="pile cch-page">
        <Onglets libelle="Espaces de conception" actif={actif} changer={changer}
          onglets={(Object.keys(LIBELLES) as OngletConception[]).map((o) => ({ id: o, libelle: COURTS[o] === LIBELLES[o] ? LIBELLES[o]
            : <><span className="masque-etroit">{LIBELLES[o]}</span><span className="visible-etroit">{COURTS[o]}</span></> }))} />
        <p className="petit secondaire cch-intro masque-etroit">{INTRO[actif]}</p>
        <div role="tabpanel" aria-label={LIBELLES[actif]}>
          {actif === 'schema' ? <OngletSchema dossierId={id} /> : actif === 'cheminements' ? <OngletCheminements dossierId={id} /> : actif === 'comptage' ? <OngletComptage dossierId={id} /> : actif === 'proposition' ? <OngletProposition dossierId={id} /> : <OngletDimensionnement dossierId={id} />}
        </div>
      </div>
    </DossierCadre>
  );
}
